// BEGIN FILE: src/utils/yogas.ts
// Classical Vedic Yoga calculations
import { DateTime } from 'luxon';
import type { PlanetName } from './astroEngine';

export interface SadeSatiPhase {
  phase: 'rising' | 'peak' | 'setting';
  from: string; // ISO date
  to: string;   // ISO date
  moonSign: number; // 1-12 (which moon sign defines this sade sati)
}

export interface SadeSatiPeriod {
  isActive: boolean;
  currentPhase?: SadeSatiPhase;
  allPhases: SadeSatiPhase[];
  totalPeriod?: { from: string; to: string };
}

export interface KaalSarpYoga {
  isPresent: boolean;
  type: 'partial' | 'full' | 'none';
  details: string;
  rahuHouse: number;
  ketuHouse: number;
  planetsBetween: PlanetName[];
  specificType?: string; // Anant, Kulik, Vasuki, etc.
  specificEffects?: string; // Detailed effects of the specific type
  hasRajYogaCombination?: boolean; // Rahu-Venus conjunction or Rahu in Venus's house
}

/**
 * Calculate Saturn Sade Sati periods
 * Sade Sati occurs when Saturn transits:
 * - 12th from Moon sign (Rising/Ascending - 2.5 years)
 * - Moon sign itself (Peak - 2.5 years)
 * - 2nd from Moon sign (Setting/Descending - 2.5 years)
 * Total: 7.5 years
 * 
 * Saturn takes ~29.5 years to complete zodiac, ~2.5 years per sign
 */
export function calculateSadeSati(params: {
  currentDate: string; // ISO date to check
  natalMoonSign: number; // 1-12
  saturnSign: number; // Current Saturn sign 1-12
  saturnSignEntryDate?: string; // When Saturn entered current sign
}): SadeSatiPeriod {
  const { currentDate, natalMoonSign, saturnSign } = params;
  
  // Calculate which signs are sade sati for this moon
  const risingSign = ((natalMoonSign - 2 + 12) % 12) + 1; // 12th from moon
  const peakSign = natalMoonSign;
  const settingSign = (natalMoonSign % 12) + 1; // 2nd from moon
  
  // Approximate Saturn transit duration: 2.5 years per sign
  const SATURN_SIGN_DURATION_DAYS = 912; // ~2.5 years
  
  // Check if currently in any sade sati phase
  let currentPhase: SadeSatiPhase | undefined;
  
  if (saturnSign === risingSign) {
    currentPhase = {
      phase: 'rising',
      from: params.saturnSignEntryDate || currentDate,
      to: DateTime.fromISO(params.saturnSignEntryDate || currentDate)
        .plus({ days: SATURN_SIGN_DURATION_DAYS })
        .toISO()!,
      moonSign: natalMoonSign
    };
  } else if (saturnSign === peakSign) {
    currentPhase = {
      phase: 'peak',
      from: params.saturnSignEntryDate || currentDate,
      to: DateTime.fromISO(params.saturnSignEntryDate || currentDate)
        .plus({ days: SATURN_SIGN_DURATION_DAYS })
        .toISO()!,
      moonSign: natalMoonSign
    };
  } else if (saturnSign === settingSign) {
    currentPhase = {
      phase: 'setting',
      from: params.saturnSignEntryDate || currentDate,
      to: DateTime.fromISO(params.saturnSignEntryDate || currentDate)
        .plus({ days: SATURN_SIGN_DURATION_DAYS })
        .toISO()!,
      moonSign: natalMoonSign
    };
  }
  
  // Build all phases (even if not currently active, for reference)
  const allPhases: SadeSatiPhase[] = [];
  
  if (currentPhase) {
    // Build sequence based on current phase
    const dt = DateTime.fromISO(currentPhase.from);
    
    if (currentPhase.phase === 'rising') {
      allPhases.push(currentPhase);
      allPhases.push({
        phase: 'peak',
        from: currentPhase.to,
        to: dt.plus({ days: SATURN_SIGN_DURATION_DAYS * 2 }).toISO()!,
        moonSign: natalMoonSign
      });
      allPhases.push({
        phase: 'setting',
        from: allPhases[1].to,
        to: dt.plus({ days: SATURN_SIGN_DURATION_DAYS * 3 }).toISO()!,
        moonSign: natalMoonSign
      });
    } else if (currentPhase.phase === 'peak') {
      allPhases.push({
        phase: 'rising',
        from: dt.minus({ days: SATURN_SIGN_DURATION_DAYS }).toISO()!,
        to: currentPhase.from,
        moonSign: natalMoonSign
      });
      allPhases.push(currentPhase);
      allPhases.push({
        phase: 'setting',
        from: currentPhase.to,
        to: dt.plus({ days: SATURN_SIGN_DURATION_DAYS * 2 }).toISO()!,
        moonSign: natalMoonSign
      });
    } else { // setting
      allPhases.push({
        phase: 'rising',
        from: dt.minus({ days: SATURN_SIGN_DURATION_DAYS * 2 }).toISO()!,
        to: dt.minus({ days: SATURN_SIGN_DURATION_DAYS }).toISO()!,
        moonSign: natalMoonSign
      });
      allPhases.push({
        phase: 'peak',
        from: allPhases[0].to,
        to: currentPhase.from,
        moonSign: natalMoonSign
      });
      allPhases.push(currentPhase);
    }
  }
  
  const totalPeriod = allPhases.length > 0 
    ? { from: allPhases[0].from, to: allPhases[allPhases.length - 1].to }
    : undefined;
  
  return {
    isActive: !!currentPhase,
    currentPhase,
    allPhases,
    totalPeriod
  };
}

/**
 * Check for Kaal Sarp Yoga (Kaal Sarpa Dosha)
 * Occurs when all 7 classical planets are hemmed between Rahu and Ketu
 * 
 * Full KSY: All planets on one side of Rahu-Ketu axis (12 specific types)
 * Partial KSY: Most planets on one side (5-6 planets)
 */
export function checkKaalSarpYoga(placements: {
  planet: PlanetName;
  house: number; // 1-12
}[]): KaalSarpYoga {
  // Find Rahu and Ketu positions
  const rahuPos = placements.find(p => p.planet === 'Rahu');
  const ketuPos = placements.find(p => p.planet === 'Ketu');
  const venusPos = placements.find(p => p.planet === 'Venus');
  
  if (!rahuPos || !ketuPos) {
    return {
      isPresent: false,
      type: 'none',
      details: 'Rahu or Ketu position not found',
      rahuHouse: 0,
      ketuHouse: 0,
      planetsBetween: []
    };
  }
  
  // Get classical 7 planets (exclude Rahu/Ketu)
  const classicalPlanets = placements.filter(p => 
    !['Rahu', 'Ketu'].includes(p.planet)
  );
  
  // Calculate which planets are between Rahu and Ketu (clockwise)
  const rahuHouse = rahuPos.house;
  const ketuHouse = ketuPos.house;
  
  const planetsBetweenRahuKetu: PlanetName[] = [];
  
  for (const p of classicalPlanets) {
    const house = p.house;
    
    // Check if house is between Rahu and Ketu (going clockwise from Rahu)
    let isBetween = false;
    if (rahuHouse < ketuHouse) {
      // No wrap: simple range check
      isBetween = house > rahuHouse && house < ketuHouse;
    } else {
      // Wrap around: planet is between if it's > rahu OR < ketu
      isBetween = house > rahuHouse || house < ketuHouse;
    }
    
    if (isBetween) {
      planetsBetweenRahuKetu.push(p.planet);
    }
  }
  
  // Determine yoga type
  const betweenCount = planetsBetweenRahuKetu.length;
  const totalPlanets = classicalPlanets.length;
  
  let isPresent = false;
  let type: 'full' | 'partial' | 'none' = 'none';
  let details = '';
  let specificType: string | undefined;
  let specificEffects: string | undefined;
  
  // Check for Raj Yoga combination: Rahu-Venus conjunction or Rahu in Venus's house
  let hasRajYogaCombination = false;
  if (venusPos) {
    // Check if Rahu and Venus are in the same house (conjunction)
    if (rahuHouse === venusPos.house) {
      hasRajYogaCombination = true;
    }
    // Check if Rahu is in a house ruled by Venus (Taurus=2, Libra=7)
    // Note: This requires sign information, approximating with houses
    // For proper implementation, pass sign data instead of just house numbers
  }
  
  // Full Kaal Sarp: All planets between Rahu and Ketu
  if (betweenCount === totalPlanets) {
    isPresent = true;
    type = 'full';
    
    // Determine specific type based on Rahu's house
    const kaalSarpTypes: Record<number, { name: string; effects: string }> = {
      1: {
        name: 'Anant Kaal Sarp',
        effects: 'Problem of personality, slow success, impacts self development.'
      },
      2: {
        name: 'Kulik Kaal Sarp',
        effects: 'Money issues, hate speech, throat problems.'
      },
      3: {
        name: 'Vasuki Kaal Sarp',
        effects: 'Problems from siblings, too much hardwork and no results.'
      },
      4: {
        name: 'Shankhpal Kaal Sarp',
        effects: 'Can make a family person ill entire life, mother health problems, property splits.'
      },
      5: {
        name: 'Padam Kaal Sarp',
        effects: 'Children with bad habits, education problems, slow learner, problems in child birth.'
      },
      6: {
        name: 'Maha Padam Kaal Sarp',
        effects: 'Job issues, upper chest illness, dental problems.'
      },
      7: {
        name: 'Takshak Kaal Sarp',
        effects: 'Marriage problem, business problem, business comes to zero.'
      },
      8: {
        name: 'Karkotak Kaal Sarp',
        effects: 'Truth speaking may land into problems, inheritance problems.'
      },
      9: {
        name: 'Shankhchud Kaal Sarp',
        effects: 'Problems from father, education problem, gets frustrated quickly, bad luck.'
      },
      10: {
        name: 'Ghatak Kaal Sarp',
        effects: 'Small profession impact, bad, no respect.'
      },
      11: {
        name: 'Vishdhar Kaal Sarp',
        effects: 'They become ghosts after death, unfulfilled desires, don\'t get moksha, enemity with friends and relatives, less achievement in life.'
      },
      12: {
        name: 'Sheshnag Kaal Sarp',
        effects: 'Wealth will get destroyed by children or spouse, delay, unnecessary expenses come.'
      }
    };
    
    const typeInfo = kaalSarpTypes[rahuHouse];
    if (typeInfo) {
      specificType = typeInfo.name;
      specificEffects = typeInfo.effects;
      
      if (hasRajYogaCombination) {
        specificEffects += ' SPECIAL: Rahu-Venus combination creates Raj Yoga - brings material success, wealth, and power but may destroy love life and relationships.';
      }
      
      details = `Full ${typeInfo.name}: All ${totalPlanets} planets between Rahu (H${rahuHouse}) and Ketu (H${ketuHouse}). ${typeInfo.effects}`;
    } else {
      details = `Full Kaal Sarp Yoga: All ${totalPlanets} planets hemmed between Rahu (H${rahuHouse}) and Ketu (H${ketuHouse}). Strong karmic effects, obstacles require spiritual remedies.`;
    }
  } else if (betweenCount === 0) {
    // All planets on Ketu's side (reverse Kaal Sarp)
    isPresent = true;
    type = 'full';
    
    // Use the same type definitions as regular Kaal Sarp - effects are based on Rahu's house
    const kaalSarpTypes: Record<number, { name: string; effects: string }> = {
      1: {
        name: 'Anant Kaal Sarp',
        effects: 'Problem of personality, slow success, impacts self development.'
      },
      2: {
        name: 'Kulik Kaal Sarp',
        effects: 'Money issues, hate speech, throat problems.'
      },
      3: {
        name: 'Vasuki Kaal Sarp',
        effects: 'Problems from siblings, too much hardwork and no results.'
      },
      4: {
        name: 'Shankhpal Kaal Sarp',
        effects: 'Can make a family person ill entire life, mother health problems, property splits.'
      },
      5: {
        name: 'Padam Kaal Sarp',
        effects: 'Children with bad habits, education problems, slow learner, problems in child birth.'
      },
      6: {
        name: 'Maha Padam Kaal Sarp',
        effects: 'Job issues, upper chest illness, dental problems.'
      },
      7: {
        name: 'Takshak Kaal Sarp',
        effects: 'Marriage problem, business problem, business comes to zero.'
      },
      8: {
        name: 'Karkotak Kaal Sarp',
        effects: 'Truth speaking may land into problems, inheritance problems.'
      },
      9: {
        name: 'Shankhchud Kaal Sarp',
        effects: 'Problems from father, education problem, gets frustrated quickly, bad luck.'
      },
      10: {
        name: 'Ghatak Kaal Sarp',
        effects: 'Small profession impact, bad, no respect.'
      },
      11: {
        name: 'Vishdhar Kaal Sarp',
        effects: 'They become ghosts after death, unfulfilled desires, don\'t get moksha, enemity with friends and relatives, less achievement in life.'
      },
      12: {
        name: 'Sheshnag Kaal Sarp',
        effects: 'Wealth will get destroyed by children or spouse, delay, unnecessary expenses come.'
      }
    };
    
    const typeInfo = kaalSarpTypes[rahuHouse];
    if (typeInfo) {
      specificType = `${typeInfo.name} (Reverse)`;
      specificEffects = typeInfo.effects;
      details = `Full ${typeInfo.name} (Reverse): All ${totalPlanets} planets on Ketu's side. ${typeInfo.effects}`;
    } else {
      specificType = 'Reverse Kaal Sarp';
      details = `Full Kaal Sarp Yoga (Reverse): All ${totalPlanets} planets on Ketu's side.`;
    }
  } else if (betweenCount >= totalPlanets - 1) {
    // Partial: 6 out of 7 planets (one planet breaks the yoga)
    isPresent = true;
    type = 'partial';
    
    const planetsNotBetween = classicalPlanets
      .filter(p => !planetsBetweenRahuKetu.includes(p.planet))
      .map(p => p.planet);
    
    details = `Partial Kaal Sarp Yoga: ${betweenCount}/${totalPlanets} planets between Rahu-Ketu axis. ${planetsNotBetween.join(', ')} break(s) the full yoga, reducing its intensity. Moderate karmic pressure in specific life areas.`;
    specificType = 'Partial Kaal Sarp';
    specificEffects = `The yoga is weakened by ${planetsNotBetween.join(', ')}. Effects are milder and more manageable. Remedies are still beneficial but less urgent.`;
  } else {
    details = `No Kaal Sarp Yoga: Planets distributed on both sides of Rahu-Ketu axis (${betweenCount}/${totalPlanets} between). No significant karmic obstruction pattern.`;
  }
  
  return {
    isPresent,
    type,
    details,
    rahuHouse,
    ketuHouse,
    planetsBetween: planetsBetweenRahuKetu,
    specificType,
    specificEffects,
    hasRajYogaCombination
  };
}

/**
 * Get Saturn transit timeline for Sade Sati tracking
 * Returns approximate dates when Saturn enters each sign
 * (In production, use Swiss Ephemeris for accurate dates)
 */
export function getSaturnTransitTimeline(fromDate: string, years: number = 30): {
  sign: number;
  entryDate: string;
  exitDate: string;
}[] {
  const timeline: { sign: number; entryDate: string; exitDate: string }[] = [];
  const SATURN_SIGN_DURATION_DAYS = 912; // ~2.5 years
  
  let currentDate = DateTime.fromISO(fromDate);
  let currentSign = 1; // Start from Aries (adjust based on actual Saturn position)
  
  const endDate = currentDate.plus({ years });
  
  while (currentDate < endDate) {
    const exitDate = currentDate.plus({ days: SATURN_SIGN_DURATION_DAYS });
    
    timeline.push({
      sign: currentSign,
      entryDate: currentDate.toISO()!,
      exitDate: exitDate.toISO()!
    });
    
    currentDate = exitDate;
    currentSign = (currentSign % 12) + 1;
  }
  
  return timeline;
}
// END FILE
